import { isEqual } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import { useDataset } from '../api/hooks/useDataset';
import { useDatasetData } from '../api/hooks/useDatasetData';
import { useFilter } from '../api/hooks/useFilter';
import { SubsetDataApiRequestData } from '../api/types/types';
import { apiRequest } from '../api/utils/apiRequest';
import { getSubsetDataApiRequestData } from '../api/utils/requestData/getSubsetDataApiRequestData';
import { GRID_DATA_LIMIT } from '../constants';
import { useDatasetContext } from '../hooks/context/useDatasetContext';
import { useResponseActionsContext } from '../hooks/context/useResponseActionsContext';
import { useTranslate } from '../hooks/context/useTranslate';
import { SlaveDatasetContextType } from '../types/context';

import { ColumnsWitdhType, Dataset, GridRow } from '../types/types';
import { getDataParamsString } from '../utils/apiRequests/getDataParamStrings';
import { getFilterParamObject } from '../utils/apiRequests/getFilterParamObject';
import { setDatasetFetchState } from '../utils/apiRequests/setDatasetFetchState';
import { updateActiveFiltersFromLazyState } from '../utils/filters/updateActiveFiltersFromLazyState';
import { resolveTemplate } from '../utils/json/resolveTemplate';
import { getColumnsWidth } from '../utils/getColumnsWidth';
import {
  getMultisortFromDataset,
  updateActiveSortingFromLazyState,
} from '../utils/managerUtils';
import { processFetchedSlaveDataset } from '../utils/processFetchedSlaveDataset';
import { processFetchedSlaveFilter } from '../utils/processFetchedSlaveFilter';
import { shouldApplyFetchedDataset } from '../utils/shouldApplyFetchedDataset';
import {
  isSlaveComboLoaded,
  patchSlaveComboParentRow,
} from '../utils/isSlaveComboLoaded';
import { useUserState } from '../hooks/context/useUserState';
import { buildLocateRequestData } from '../utils/dataset/buildLocateRequestData';
import { fetchRowIds } from '../utils/dataset/fetchRowIds';
import { computeCustomLocateLimit } from '../utils/dataset/computeCustomLocateLimit';
import { getDataFromDatasetRequest } from '../api/utils/getDataFromDatasetRequest';
import { useDatasetVisibilityResolve } from '../hooks/useDatasetVisibilityResolve';
import { resolveDatasetItemsVisibility } from '../utils/dataset/resolveDatasetItemsVisibility';
import { probeRowIndexesByIds } from '../utils/dataset/probeRowIndexesByIds';
import { withSyntheticRowIds } from '../utils/withSyntheticRowIds';
import { syncCachedQueryIdRef } from '../utils/dataset/syncCachedQueryIdRef';

const defaultValues: SlaveDatasetContextType = {
  isProvided: false,
  datasetError: undefined,
  dataError: undefined,
  filterError: undefined,
  datasetLoading: true,
  dataset: undefined,
  data: null,
  gridLoading: true,
  activeFilters: null,
  activeSorting: [],
  datasetData: undefined,
  expandedRow: null,
  lazyLoading: false,
  dataKey: '_id',
  disabledLazyLoading: false,
  columnsWidth: undefined,
  cachedQueryId: '',
  subsetId: null,
  requestData: undefined,
  removeRowsLocally: () => {
    throw new Error('removeRowsLocally must be implemented');
  },
  patchRowLocally: () => {
    throw new Error('patchRowLocally must be implemented');
  },
  invalidateFetchedCombo: () => {
    throw new Error('invalidateFetchedCombo must be implemented');
  },
  acknowledgeParentRowPatch: () => {
    throw new Error('acknowledgeParentRowPatch must be implemented');
  },
  setParentRow: () => {
    throw new Error('setParentRow must be implemented');
  },
  setSubsetId: () => {
    throw new Error('setSubsetId must be implemented');
  },
  setExpandedRow: () => {
    throw new Error('setExpandedRow must be implemented');
  },
  fetchDatasetData: () => {
    throw new Error('fetchDatasetData must be implemented');
  },
  loadMoreData: () => {
    throw new Error('loadMoreData must be implemented');
  },
  locateAndFetchById: () => {
    throw new Error('locateAndFetchById must be implemented');
  },
  locateAndFetchFarthestByIds: () => {
    throw new Error('locateAndFetchFarthestByIds must be implemented');
  },
};

export const SlaveDatasetContext =
  createContext<SlaveDatasetContextType>(defaultValues);

const SlaveDatasetContextProvider = ({ children }: { children: ReactNode }) => {
  const [parentRow, setParentRow] = useState<GridRow | null>(null);
  const [subsetId, setSubsetId] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState(defaultValues.expandedRow);
  const [datasetLoading, setDatasetLoading] = useState(
    defaultValues.datasetLoading
  );
  const [data, setData] = useState<any[] | undefined>(defaultValues.data);
  const [gridLoading, setGridLoading] = useState(defaultValues.gridLoading);
  const [lazyLoading, setLazyLoading] = useState(defaultValues.lazyLoading);
  const [cachedQueryId, setCachedQueryId] = useState(
    defaultValues.cachedQueryId
  );
  const [offset, setOffset] = useState(0);
  const [disabledLazyLoading, setDisabledLazyLoading] = useState(
    defaultValues.disabledLazyLoading
  );
  const [columnsWidth, setColumnsWidth] = useState<ColumnsWitdhType>(
    defaultValues.columnsWidth
  );
  const [dataset, setDataset] = useState<any>(undefined);
  const [activeFilters, setActiveFilters] = useState<any | null>(
    defaultValues.activeFilters
  );
  const [activeSorting, setActiveSorting] = useState<any[]>(
    defaultValues.activeSorting
  );

  // Item `visible` expressions resolved to booleans; unresolved expression items stay hidden
  const visibilityResolvedDataset = useDatasetVisibilityResolve(
    dataset,
    activeFilters
  );

  const {
    state: { token },
  } = useUserState();

  const {
    slaveLazyStates,
    slaveDatasets,
    slaveFilters,
    setSlaveDatasets,
    setSlaveLazyStates,
    setSlaveFilters,
  } = useDatasetContext();
  const { getResponseActionHandler } = useResponseActionsContext();

  const {
    data: fetchedDataset,
    fetch: fetchDataset,
    error: datasetError,
  } = useDataset(subsetId || '');

  const {
    data: datasetData,
    fetch: fetchData,
    detailedFetch: detailedFetchData,
    error: dataError,
    isLoading: dataLoading,
  } = useDatasetData();

  const {
    data: filter,
    fetch: fetchFilter,
    error: filterError,
    isLoading: filterLoading,
  } = useFilter();

  const cachedQueryIdRef = useRef(cachedQueryId);
  const disabledLazyLoadinRef = useRef(disabledLazyLoading);
  const slaveLazyState: any = useMemo(() => {
    if (!subsetId) {
      return null;
    }
    return slaveLazyStates[subsetId];
  }, [subsetId, slaveLazyStates]);
  //TODO: lazyState type should be DataTableStateEvent, but it does not accept filters null
  const lazyStateRef = useRef(slaveLazyState);
  const offsetRef = useRef(offset);
  const lazyLoadingRef = useRef(lazyLoading);
  const gridLoadingRef = useRef(gridLoading);
  const activeFiltersRef = useRef(activeFilters);
  const activeSortingRef = useRef(activeSorting);
  // Tracks the (subsetId, parentRow, activeFilters, activeSorting) combo last
  // sent to the backend. Updated after the fetch effect actually dispatches.
  // - The switch effect reads {subsetId, parentRow} to distinguish a real
  //   switch from a SlaveTabs remount on the same combo (e.g. returning from
  //   the Detailed Filter page with no filter change): skip clearing data /
  //   gridLoading so the grid is not stuck on a spinner when no fetch follows.
  // - The fetch effect reads the full combo to skip redundant refetches on
  //   pure slave <-> detail tab toggles while still firing on real changes.
  const lastFetchedComboRef = useRef<{
    subsetId: string | null;
    parentRow: GridRow | null;
    activeFilters: any;
    activeSorting: any[] | null;
  }>({
    subsetId: null,
    parentRow: null,
    activeFilters: null,
    activeSorting: null,
  });
  const acknowledgeParentRowPatch = useCallback(
    (patch: GridRow): void => {
      lastFetchedComboRef.current = patchSlaveComboParentRow(
        lastFetchedComboRef.current,
        patch
      );
    },
    []
  );
  const dataKey = dataset?.idItem || defaultValues.dataKey;

  const { t } = useTranslate();

  const selectDataset = (newDataset) => {
    setDataset(newDataset);
    const newActiveFilters =
      slaveLazyState && slaveLazyState.filters !== null
        ? updateActiveFiltersFromLazyState(null, slaveLazyState, newDataset)
        : {};
    setActiveFilters((oldActiveFilters) =>
      isEqual(oldActiveFilters, newActiveFilters)
        ? oldActiveFilters
        : newActiveFilters
    );
    setDatasetLoading(false);
  };

  const fetchDatasetData = async (
    dataset: Dataset,
    lazy = false,
    queryId?: string,
    activeSorting?: any[],
    activeFilter?: Record<string, unknown>,
    customLimit?: number,
    customOffset?: number,
    // Fills datasetData.totalCount when the server omits the header
    // (customLimit locate responses) — see getResult in useDatasetData.
    fallbackTotalCount?: number
  ) => {
    if (!parentRow) {
      return;
    }

    if (!lazy && !queryId?.trim()) {
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
    }

    const limit = customLimit || GRID_DATA_LIMIT;
    let dataOffset = 0;

    if (lazy) {
      dataOffset = offsetRef.current + limit;
    }

    if (customOffset !== undefined) {
      dataOffset = customOffset;
    }

    const resolvedActiveFilters =
      activeFilter || activeFiltersRef.current || {};
    const resolvedActiveSorting =
      activeSorting || activeSortingRef.current || [];

    setDatasetFetchState(
      dataOffset,
      lazy,
      setOffset,
      setDisabledLazyLoading,
      customLimit
    );

    const filters = getFilterParamObject(resolvedActiveFilters);
    // Items param must reflect dynamic `visible` state for the filters of this fetch
    const requestDataset = await resolveDatasetItemsVisibility(
      dataset,
      resolvedActiveFilters
    );
    const requestData: SubsetDataApiRequestData = getSubsetDataApiRequestData(
      (parentRow[dataKey] as any).toString(),
      requestDataset,
      resolvedActiveSorting,
      token,
      dataOffset,
      filters,
      limit,
      // A fresh request omits cachedQueryId; lazy/locate requests reuse it.
      queryId
    );

    // String api method request flow
    if (typeof dataset.apiMethod === 'string') {
      const { url } = await resolveTemplate(
        { url: dataset.apiMethod },
        requestData
      );

      const paramsString = getDataParamsString(
        resolvedActiveFilters,
        resolvedActiveSorting,
        requestDataset,
        limit,
        dataOffset
      );

      const result = await fetchData(
        `${url}${paramsString}`,
        dataset,
        lazy,
        queryId,
        undefined,
        undefined,
        undefined,
        fallbackTotalCount
      );
      syncCachedQueryIdRef(result, lazy, cachedQueryIdRef, subsetId);
      return result?.data;
    }

    // Object api method request flow
    return await apiRequest(
      dataset.apiMethod,
      requestData,
      (apiMethod) =>
        getDataFromDatasetRequest(async () => {
          const res = await detailedFetchData(
            apiMethod,
            dataset,
            lazy,
            undefined,
            undefined,
            undefined,
            fallbackTotalCount
          );
          // Before apiRequest runs responseActions — a handler that refetches
          // must not have its fresher id overwritten by this response.
          syncCachedQueryIdRef(res, lazy, cachedQueryIdRef, subsetId);
          return res;
        }),
      getResponseActionHandler
    );
  };

  const requestData = useMemo(() => {
    if (!visibilityResolvedDataset || !parentRow) return undefined;

    const filtersParam = getFilterParamObject(activeFiltersRef.current || {});
    return getSubsetDataApiRequestData(
      parentRow[dataKey] as any,
      visibilityResolvedDataset,
      activeSortingRef.current || [],
      token,
      0,
      filtersParam
    );
  }, [
    visibilityResolvedDataset,
    parentRow,
    dataKey,
    activeFilters,
    activeSorting,
  ]);

  // Drops the last-fetched combo so the next parentRow/subsetId write always
  // fetches. Needed when the server mutated subset rows without touching the
  // master row values — the deep-equal combo guards below would otherwise
  // skip the refetch (this provider survives page changes, so the combo does too).
  const invalidateFetchedCombo = useCallback(() => {
    lastFetchedComboRef.current = {
      subsetId: null,
      parentRow: null,
      activeFilters: null,
      activeSorting: null,
    };
  }, []);

  const removeRowsLocally = useCallback(
    (ids: string[]) => {
      if (!dataset?.idItem) {
        console.warn(
          '[removeRows] slave dataset has no idItem — cannot remove rows locally'
        );
        return;
      }
      const idSet = new Set(ids.map(String));
      setData((old) => old?.filter((row) => !idSet.has(String(row[dataKey]))));
    },
    [dataKey, dataset?.idItem]
  );

  const patchRowLocally = useCallback(
    (id: string, patch: Record<string, unknown>) => {
      if (!dataset?.idItem) {
        console.warn(
          '[patchRow] slave dataset has no idItem — cannot patch row locally'
        );
        return;
      }
      if (!patch || Object.keys(patch).length === 0) return;
      setData((old) =>
        old?.map((row) =>
          String(row[dataKey]) === String(id) ? { ...row, ...patch } : row
        )
      );
    },
    [dataKey, dataset?.idItem]
  );

  const loadMoreData = () => {
    if (
      data?.length === datasetData?.totalCount ||
      datasetData?.totalCount === undefined
    )
      return;

    if (
      !lazyLoadingRef.current &&
      !gridLoadingRef.current &&
      !disabledLazyLoadinRef.current &&
      dataset
    ) {
      setLazyLoading(true);
      lazyLoadingRef.current = true;
      fetchDatasetData(dataset, true, cachedQueryIdRef.current).then((rows) => {
        // Failed/dropped response never reaches the datasetData effect that
        // clears the flag — release it here or lazy loading deadlocks.
        if (rows === undefined) {
          setLazyLoading(false);
          lazyLoadingRef.current = false;
        }
      });
    }
  };

  const locateAndFetchById = async (
    itemId: string,
    forcedNewData: boolean,
    mismatchCallback?: () => void,
    cleanup?: () => void
  ): Promise<void> => {
    // All filtered rows are already loaded; if not in `data`, skip probe and
    // surface "not found" immediately.
    if (
      data &&
      datasetData?.totalCount !== undefined &&
      data.length >= datasetData.totalCount &&
      forcedNewData === false
    ) {
      toast(t('toasts.rowNotFound'));
      cleanup?.();
      return;
    }

    if (forcedNewData) {
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
    }

    // Lock cachedQueryId once so probe and customLimit fetch use the same
    // server cache; this keeps row ordering and probe index alignment stable.
    const lockedCachedQueryId = cachedQueryIdRef.current;

    const { idItem, resolvedFilters, resolvedSorting, requestData } =
      buildLocateRequestData(
        dataset,
        activeFilters,
        activeSorting,
        token,
        String(parentRow?.[dataKey]),
        lockedCachedQueryId || null
      );

    const probe = await fetchRowIds({
      dataset,
      requestData,
      resolvedFilters,
      resolvedSorting,
      idItem,
      sessionToken: token,
    });

    if (!probe) {
      toast(t('toasts.rowNotFound'));
      cleanup?.();
      return;
    }

    // Probe cache differs from the locked initial cache, so any computed index
    // would target stale data.
    if (
      lockedCachedQueryId &&
      probe.cachedQueryId &&
      probe.cachedQueryId !== lockedCachedQueryId
    ) {
      toast(t('toasts.newDataLoaded'));
      mismatchCallback?.();
      cleanup?.();
      return;
    }

    const index = probe.ids.findIndex(
      (row) => String(row?.[idItem]) === itemId
    );
    if (index === -1) {
      toast(t('toasts.rowNotFound'));
      cleanup?.();
      return;
    }

    // No clamp to a total: datasetData.totalCount can be stale (an external
    // single-record fetch leaves it at 1, which would shrink the window to one
    // row). Rounding the probe index up to the next page chunk already
    // guarantees the row is inside the fetched payload; the server returns only
    // what exists.
    const locateLimit = computeCustomLocateLimit(index);

    await fetchDatasetData(
      dataset,
      false,
      lockedCachedQueryId,
      resolvedSorting,
      resolvedFilters,
      locateLimit
    );

    // Server omits X-CACHEDQUERY-TOTALCNT on the locate response (esp. with
    // empty cachedQueryId), so the datasetData effect's reachedEnd fallback
    // wrongly treats a full payload (>= GRID_DATA_LIMIT) as partial.
    // probe.ids is the authoritative row count — flip the flag directly.
    if (probe.ids.length <= locateLimit) {
      setDisabledLazyLoading(true);
      disabledLazyLoadinRef.current = true;
    }
  };

  // Multi-id sibling of locateAndFetchById — subset variant of the master
  // method in DatasetContext (probe scoped to the expanded master row).
  // onLocated fires with the farthest row's id BEFORE the fetch so the caller
  // can arm the grid scroll. Any failure = silent no-op, page-1 data stays.
  const locateAndFetchFarthestByIds = async (
    itemIds: string[],
    onLocated?: (farthestId: string) => void
  ): Promise<void> => {
    if (itemIds.length === 0) return;
    if (!dataset?.apiMethod || !parentRow) return;

    // Lock cachedQueryId once so probe and customLimit fetch use the same
    // server cache; this keeps row ordering and probe index alignment stable.
    const lockedCachedQueryId = cachedQueryIdRef.current;

    const probeResult = await probeRowIndexesByIds({
      dataset,
      activeFilters,
      activeSorting,
      sessionToken: token,
      lockedCachedQueryId,
      itemIds,
      masterRowId: String(parentRow[dataKey]),
    });
    if (!probeResult) return;

    onLocated?.(probeResult.farthestId);

    const locateLimit = computeCustomLocateLimit(probeResult.farthestIndex);
    // totalRowCount backfills the missing totalCount header so lazy loading
    // keeps working past the fetched window.
    await fetchDatasetData(
      dataset,
      false,
      lockedCachedQueryId,
      probeResult.resolvedSorting,
      probeResult.resolvedFilters,
      locateLimit,
      undefined,
      probeResult.totalRowCount
    );

    if (probeResult.totalRowCount <= locateLimit) {
      setDisabledLazyLoading(true);
      disabledLazyLoadinRef.current = true;
    }
  };

  useEffect(() => {
    lazyStateRef.current = slaveLazyState;
    if (dataset && data && columnsWidth && slaveLazyState?.multiSortMeta) {
      const newColumnsWidth = getColumnsWidth(
        dataset,
        data,
        columnsWidth,
        slaveLazyState,
        true
      );
      setColumnsWidth(newColumnsWidth);
    }
  }, [slaveLazyState]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    lazyLoadingRef.current = lazyLoading;
  }, [lazyLoading]);

  useEffect(() => {
    gridLoadingRef.current = gridLoading;
  }, [gridLoading]);

  useEffect(() => {
    cachedQueryIdRef.current = cachedQueryId;
  }, [cachedQueryId]);

  useEffect(() => {
    disabledLazyLoadinRef.current = disabledLazyLoading;
  }, [disabledLazyLoading]);

  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    activeSortingRef.current = activeSorting;
  }, [activeSorting]);

  // Trigger data refresh when parent row OR active tab changes
  useEffect(() => {
    if (subsetId) {
      // Skip when re-entering the same (subsetId, parentRow) we last
      // fetched. Happens on SlaveTabs remount (e.g. closing Detailed Filter
      // with no filter change): subsetId goes B -> null -> B while parentRow
      // stays put. Filter submits update slaveLazyStates and are handled by
      // the slaveLazyState effect below, which drives activeFilters + fetch.
      const alreadyFetched =
        data != null &&
        lastFetchedComboRef.current.subsetId === subsetId &&
        isEqual(lastFetchedComboRef.current.parentRow, parentRow);
      if (alreadyFetched) {
        return;
      }

      // Clear stale rows + show spinner up-front so the previous subset's
      // data does not flash before the downstream fetch kicks in
      // (selectDataset -> activeFilters change -> fetchDatasetData).
      setData(undefined);
      setGridLoading(true);
      setColumnsWidth(undefined);
      setOffset(0);
      setCachedQueryId('');
      setDisabledLazyLoading(false);
      setLazyLoading(false);
      setExpandedRow(null);
      const slaveDataset = slaveDatasets[subsetId];
      if (slaveDataset && slaveLazyState) {
        const newActiveSorting = updateActiveSortingFromLazyState(
          [],
          slaveLazyState
        );
        setActiveSorting((oldActiveSorting) =>
          isEqual(oldActiveSorting, newActiveSorting)
            ? oldActiveSorting
            : newActiveSorting
        );
        selectDataset(slaveDataset);
      } else {
        setDatasetLoading(true);
        fetchDataset();
      }
    }
  }, [parentRow, subsetId]);

  useEffect(() => {
    setGridLoading(dataLoading);
  }, [dataLoading]);

  useEffect(() => {
    if (shouldApplyFetchedDataset(fetchedDataset, subsetId)) {
      processFetchedSlaveDataset(
        fetchedDataset,
        subsetId,
        setSlaveDatasets,
        setSlaveLazyStates
      );
      const multiSortMeta = getMultisortFromDataset(fetchedDataset);
      const newActiveSorting = multiSortMeta || [];
      setActiveSorting(newActiveSorting);
      selectDataset(fetchedDataset);
    }
  }, [fetchedDataset]);

  useEffect(() => {
    if (dataset && !datasetLoading && slaveLazyState) {
      setActiveSorting((oldActiveSorting) =>
        updateActiveSortingFromLazyState(oldActiveSorting, slaveLazyState)
      );
      if (slaveLazyState.filters !== null) {
        setActiveFilters((oldActiveFilters) =>
          updateActiveFiltersFromLazyState(
            oldActiveFilters,
            slaveLazyState,
            dataset
          )
        );
      }
    }
  }, [slaveLazyState]);

  useEffect(() => {
    if (
      !datasetLoading &&
      dataset?.apiMethod &&
      dataset?.datasetId === subsetId &&
      activeFilters !== null &&
      activeSorting !== null
    ) {
      // Deep-equal combo (incl. parentRow by value) so a form edit that mutated parentRow fields still refetches.
      const sameAsLastFetched = isSlaveComboLoaded(
        lastFetchedComboRef.current,
        { subsetId, parentRow, activeFilters, activeSorting },
        data
      );

      if (sameAsLastFetched) {
        return;
      }
      lastFetchedComboRef.current = {
        subsetId,
        parentRow,
        activeFilters,
        activeSorting,
      };
      fetchDatasetData(dataset);
    }
  }, [
    datasetLoading,
    activeFilters,
    activeSorting,
    parentRow,
    subsetId,
    dataset,
  ]);

  useEffect(() => {
    // Prevents bugs with data requested before page change
    if (datasetData && datasetData.datasetId === subsetId) {
      let newData: any[] = [];
      if (
        datasetData.data.length < GRID_DATA_LIMIT ||
        (datasetData.totalCount !== undefined &&
          datasetData.data.length >= datasetData.totalCount)
      ) {
        setDisabledLazyLoading(true);
      }
      // add unique row id to datasets that have no unique id
      const resolveRows = (startIndex: number): any[] =>
        dataset?.idItem
          ? datasetData.data
          : withSyntheticRowIds(datasetData.data, startIndex);
      newData = resolveRows(0);
      if (dataset && slaveLazyState) {
        const newColumnsWidth = getColumnsWidth(
          dataset,
          newData,
          columnsWidth,
          slaveLazyState
        );
        setColumnsWidth(newColumnsWidth);
        if (datasetData.lazy) {
          setLazyLoading(false);
          if (
            datasetData.cachedQueryId === cachedQueryIdRef.current ||
            // Use lazyloading withoud server cache id if server did not return X-CACHEDQUERY-ID
            datasetData.cachedQueryId === undefined
          ) {
            setData((oldData) => {
              if (!oldData) {
                return newData;
              }
              return [...oldData, ...resolveRows(oldData.length)];
            });
          } else {
            // Reset data if X-CACHEDQUERY-ID changed
            toast(t('toasts.newDataLoaded'));
            setOffset(0);
            setData(newData);
          }
        } else {
          setData(newData);
        }
        if (datasetData.cachedQueryId !== undefined) {
          setCachedQueryId(datasetData.cachedQueryId);
          // Sync ref synchronously so an immediate follow-up lazy load reads
          // the fresh cachedQueryId instead of the stale value (parity with
          // the master grid effect in DatasetContext).
          cachedQueryIdRef.current = datasetData.cachedQueryId;
        }

        if (
          dataset.filterId &&
          !slaveFilters[dataset.datasetId] &&
          !filterLoading
        ) {
          fetchFilter(dataset.filterId, dataset.datasetId);
        }
      }
    }
  }, [datasetData]);

  const setSlaveFilter = (newFilter) =>
    setSlaveFilters((v) => ({
      ...v,
      [subsetId || '']: newFilter,
    }));

  useEffect(() => {
    if (subsetId) {
      processFetchedSlaveFilter(
        filter,
        subsetId,
        dataset,
        setSlaveLazyStates,
        setSlaveFilter
      );
    }
  }, [filter]);

  /*CONTEXT VALUE*/
  const value = useMemo(
    () => ({
      isProvided: true,
      datasetError,
      dataError,
      filterError,
      datasetLoading,
      dataset: visibilityResolvedDataset,
      data,
      gridLoading,
      activeFilters,
      activeSorting,
      datasetData,
      expandedRow,
      lazyLoading,
      dataKey,
      columnsWidth,
      cachedQueryId,
      disabledLazyLoading,
      requestData,
      subsetId,
      locateAndFetchById,
      locateAndFetchFarthestByIds,
      removeRowsLocally,
      patchRowLocally,
      invalidateFetchedCombo,
      acknowledgeParentRowPatch,
      setParentRow,
      setSubsetId,
      fetchDatasetData,
      setExpandedRow,
      loadMoreData,
    }),
    [
      datasetError,
      dataError,
      filterError,
      datasetLoading,
      visibilityResolvedDataset,
      data,
      gridLoading,
      activeFilters,
      activeSorting,
      datasetData,
      expandedRow,
      lazyLoading,
      dataKey,
      columnsWidth,
      cachedQueryId,
      disabledLazyLoading,
      requestData,
      subsetId,
      locateAndFetchById,
      locateAndFetchFarthestByIds,
      removeRowsLocally,
      patchRowLocally,
      invalidateFetchedCombo,
      acknowledgeParentRowPatch,
      setParentRow,
      setSubsetId,
      fetchDatasetData,
      setExpandedRow,
      loadMoreData,
    ]
  );

  return (
    <SlaveDatasetContext.Provider value={value}>
      {children}
    </SlaveDatasetContext.Provider>
  );
};

export default SlaveDatasetContextProvider;
